"use client";

import React, { useState, useEffect, useRef } from "react";

interface CurrencyInputProps {
  value: number;
  onChange: (val: number) => void;
  currencySymbol?: string;
  currencyCode?: string;
  locale?: string;
  label?: string;
  id?: string;
  placeholder?: string;
  max?: number;
  min?: number;
}

export default function CurrencyInput({
  value,
  onChange,
  currencySymbol = "$",
  currencyCode = "USD",
  locale = "en-US",
  label,
  id,
  placeholder = "0.00",
  max = 999999999999,
  min = 0,
}: CurrencyInputProps) {
  const [rawInput, setRawInput] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isUpdatingRef = useRef(false);

  const formatWithLocale = (num: number): string => {
    if (num === 0) return "";
    return new Intl.NumberFormat(locale, {
      useGrouping: true,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const toRawString = (num: number): string => {
    if (num === 0) return "";
    return String(num);
  };

  const cleanToNumber = (str: string): number => {
    const cleaned = str.replace(/[^0-9.]/g, "");
    const parsed = parseFloat(cleaned);
    if (isNaN(parsed)) return 0;
    return Math.max(min, Math.min(max, parsed));
  };

  // Sync rawInput from prop value when not focused
  useEffect(() => {
    if (!isFocused && !isUpdatingRef.current) {
      setRawInput(toRawString(value));
    }
  }, [value, isFocused]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    isUpdatingRef.current = true;

    const raw = e.target.value;
    let cleaned = raw.replace(/[^0-9.]/g, "");
    const parts = cleaned.split(".");
    if (parts.length > 2) {
      cleaned = parts[0] + "." + parts.slice(1).join("");
    }

    setRawInput(cleaned);

    const num = cleanToNumber(cleaned);
    onChange(num);

    isUpdatingRef.current = false;
  };

  const handleFocus = () => {
    setIsFocused(true);
    setRawInput(toRawString(value));
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (value === 0) {
      setRawInput("");
    } else {
      setRawInput(formatWithLocale(value));
    }
  };

  return (
    <div className="currency-input-wrapper">
      {label && <label htmlFor={id} className="input-label">{label}</label>}
      <div className={`currency-input-container ${isFocused ? "focused" : ""}`}>
        <div className="currency-prefix">
          <span className="currency-symbol">{currencySymbol}</span>
        </div>
        <input
          ref={inputRef}
          type="text"
          id={id}
          inputMode="decimal"
          className="currency-input-field"
          value={isFocused ? rawInput : (rawInput || "")}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
        />
        <div className="currency-suffix">
          <span className="currency-code">{currencyCode}</span>
        </div>
      </div>

      <style jsx>{`
        .currency-input-wrapper {
          width: 100%;
          margin-bottom: 0;
        }
        .currency-input-container {
          display: flex;
          align-items: stretch;
          background-color: var(--bg-input);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-md);
          overflow: hidden;
          transition: all var(--transition-fast);
          height: 3rem;
        }
        .currency-input-container.focused {
          border-color: var(--primary);
          box-shadow: 0 0 0 3px var(--primary-light);
        }
        .currency-prefix {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 0.875rem;
          background-color: var(--bg-surface-hover);
          border-right: 1px solid var(--border-color);
          min-width: 3rem;
        }
        .currency-symbol {
          color: var(--text-secondary);
          font-weight: var(--fw-bold);
          font-size: var(--text-base);
        }
        .currency-input-field {
          flex: 1;
          border: none;
          background: transparent;
          color: var(--text-primary);
          font-family: var(--font-body);
          font-size: var(--text-base);
          font-weight: var(--fw-bold);
          padding: 0 1rem;
          outline: none;
          min-width: 0;
          text-align: center;
        }
        .currency-input-field::placeholder {
          color: var(--text-muted);
          opacity: 0.5;
        }
        .currency-suffix {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 0.875rem;
          background-color: var(--bg-surface-hover);
          border-left: 1px solid var(--border-color);
          min-width: 3.5rem;
        }
        .currency-code {
          color: var(--text-muted);
          font-size: var(--text-2xs);
          font-weight: var(--fw-extrabold);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
      `}</style>
    </div>
  );
}
